/**
 * Relatos crypto core — board group keys and HKDF epoch ratcheting.
 *
 * Threat-model notes (see CLAUDE.md §1, §3):
 * - An epoch key is 32 bytes of CSPRNG output. Epoch 1 is created with the board.
 * - Rotation on membership change: key(n+1) = HKDF-SHA256(ikm = key(n),
 *   salt = fresh 32-byte random value chosen by the admin, info = "relatos/epoch").
 *   The HKDF step is deterministic, but the salt is secret and distributed ONLY to
 *   remaining members (inside NIP-44 key-update events). A removed member holds
 *   key(n) but not salt(n+1), so they cannot derive key(n+1).
 * - Chaining through the old key means compromise of one epoch key alone does not
 *   yield other epochs without each epoch's salt.
 */
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes, randomBytes, utf8ToBytes } from '@noble/hashes/utils.js';
import { getPublicKey } from 'nostr-tools';

export const EPOCH_INFO = 'relatos/epoch';

export interface EpochSecret {
  epoch: number;
  /** 32-byte board key for this epoch, hex */
  key: string;
}

export function generateBoardKey(): EpochSecret {
  return { epoch: 1, key: bytesToHex(randomBytes(32)) };
}

/** Admin side: rotate to the next epoch. Returns the new key and the salt members need. */
export function rotateEpoch(prevKeyHex: string, nextEpoch: number): EpochSecret & { salt: string } {
  const salt = bytesToHex(randomBytes(32));
  return { ...deriveEpochKey(prevKeyHex, salt, nextEpoch), salt };
}

/** Member side: derive the next epoch key from the previous key + distributed salt. */
export function deriveEpochKey(prevKeyHex: string, saltHex: string, epoch: number): EpochSecret {
  const key = hkdf(sha256, hexToBytes(prevKeyHex), hexToBytes(saltHex), utf8ToBytes(EPOCH_INFO), 32);
  return { epoch, key: bytesToHex(key) };
}

export { bytesToHex, hexToBytes, randomBytes, utf8ToBytes };

/**
 * Per-board pseudonymous posting key (review: break cross-board pubkey
 * correlation). A stable identity pubkey posting to many boards is fully
 * profileable by any relay WITHOUT decryption — so every board membership
 * gets its own throwaway secp256k1 keypair, generated at join and persisted
 * alongside the epoch keys. Relays see an unlinkable per-board author; the
 * member's identity key appears only inside the encrypted join payload and
 * in gift-wrapped key distribution.
 */
export function generatePseudoKey(): { sk: string; pk: string } {
  const skBytes = randomBytes(32);
  return { sk: bytesToHex(skBytes), pk: getPublicKey(skBytes) };
}
